import React, { useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { Calendar, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const BookingTrendsChart = ({ data = {}, loading = false, title = "Booking Trends" }) => {
  const [timeRange, setTimeRange] = useState('7') // 7, 14, 30 days

  // Check if we have valid data from backend
  const hasData = data && (data.labels?.length > 0 || data.bookings?.length > 0)
  
  if (!hasData && !loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg mr-3">
              <BarChart3 className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">No data available</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
            </select>
          </div>
        </div>
        
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No booking data available</p>
            <p className="text-xs text-gray-400 mt-1">
              Data will appear here once bookings are made
            </p>
          </div>
        </div>
      </div>
    )
  }

  const chartData = data

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#f97316',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context) => `Date: ${context[0].label}`,
          label: (context) => {
            if (context.datasetIndex === 0) {
              return `Bookings: ${context.parsed.y}`
            } else {
              return `Revenue: ₹${context.parsed.y.toLocaleString()}`
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6b7280'
        }
      },
      y: {
        beginAtZero: true,
        position: 'left',
        grid: {
          color: '#f3f4f6',
          drawBorder: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6b7280'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6b7280',
          callback: function(value) {
            return '₹' + (value / 1000).toFixed(0) + 'k'
          }
        }
      }
    }
  }

  const chartDataConfig = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Bookings',
        data: chartData.bookings,
        backgroundColor: 'rgba(249, 115, 22, 0.8)',
        borderColor: '#f97316',
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y'
      },
      {
        label: 'Revenue (₹)',
        data: chartData.revenue,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y1'
      }
    ]
  }

  // Calculate statistics
  const totalBookings = chartData.bookings?.reduce((a, b) => a + b, 0) || 0
  const totalRevenue = chartData.revenue?.reduce((a, b) => a + b, 0) || 0
  const avgBookingsPerDay = totalBookings / (chartData.bookings?.length || 1)
  const avgRevenuePerBooking = totalBookings > 0 ? totalRevenue / totalBookings : 0

  // Calculate trend (compare first half vs second half)
  const bookingsData = chartData.bookings || []
  if (bookingsData.length === 0) {
    return null // Don't render if no data
  }
  
  const midPoint = Math.floor(bookingsData.length / 2)
  const firstHalf = bookingsData.slice(0, midPoint)
  const secondHalf = bookingsData.slice(midPoint)
  const firstHalfAvg = firstHalf.length > 0 ? firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length : 0
  const secondHalfAvg = secondHalf.length > 0 ? secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length : 0
  const trend = secondHalfAvg > firstHalfAvg
  const trendPercentage = firstHalfAvg > 0 ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg * 100).toFixed(1) : 0

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-200 rounded w-48"></div>
            <div className="h-8 bg-gray-200 rounded w-32"></div>
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-2 bg-orange-100 rounded-lg mr-3">
            <BarChart3 className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">Bookings and revenue trends</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
          </select>
          {trend ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={`text-sm font-medium ${trend ? 'text-green-600' : 'text-red-600'}`}>
            {trend ? '+' : ''}{trendPercentage}%
          </span>
        </div>
      </div>
      
      <div className="h-64">
        <Bar data={chartDataConfig} options={options} />
      </div>
      
      <div className="mt-4 grid grid-cols-4 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {totalBookings}
          </div>
          <div className="text-xs text-gray-500">Total Bookings</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            ₹{(totalRevenue / 1000).toFixed(0)}k
          </div>
          <div className="text-xs text-gray-500">Total Revenue</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {avgBookingsPerDay.toFixed(1)}
          </div>
          <div className="text-xs text-gray-500">Daily Average</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">
            ₹{avgRevenuePerBooking.toFixed(0)}
          </div>
          <div className="text-xs text-gray-500">Avg per Booking</div>
        </div>
      </div>
    </div>
  )
}

export default BookingTrendsChart
